import { useState } from "react";
import { Mail, MessageCircle, Phone, CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/config/site";
import { api, errorText, hasBackend } from "@/lib/api";
import { Reveal } from "./Reveal";
import { SplitWords } from "./Motion";

const empty = { name: "", contact: "", service: "", brief: "" };
const field = "w-full rounded-2xl border-2 border-white/0 bg-white px-4 py-3.5 text-slate-900 placeholder:text-slate-500 outline-none transition-colors focus:border-[#FF5500]";

const ContactLinks = () => (
  <div className="mt-10 space-y-3">
    <a data-testid="contact-whatsapp-link" href={`https://wa.me/${SITE.contact.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-2xl bg-[#84CC16] px-5 py-4 font-bold text-slate-900 transition-transform hover:-translate-y-0.5"><MessageCircle className="h-5 w-5" /> Chat on WhatsApp</a>
    <a data-testid="contact-email-link" href={`mailto:${SITE.contact.email}`} className="flex items-center gap-3 rounded-2xl bg-white px-5 py-4 font-bold text-slate-900 transition-colors hover:bg-white/70"><Mail className="h-5 w-5" /> {SITE.contact.email}</a>
    <a data-testid="contact-phone-link" href={`tel:${SITE.contact.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 rounded-2xl bg-white px-5 py-4 font-bold text-slate-900 transition-colors hover:bg-white/70"><Phone className="h-5 w-5" /> {SITE.contact.phone}</a>
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.contact.trim().length < 5 || form.brief.trim().length < 10) {
      setErr("Please add your name, an email or phone number, and a short project description (10+ characters).");
      return;
    }
    if (!hasBackend) { setErr("The enquiry form is offline. Please email or WhatsApp us instead."); return; }
    setErr(""); setStatus("sending");
    try {
      await api.post("/enquiries", form);
      setStatus("done"); setForm(empty);
      toast.success("Thanks! Your enquiry has been sent.");
    } catch (ex) {
      setStatus("idle"); setErr(errorText(ex));
      toast.error("Couldn't send your enquiry. Please try again.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="scroll-mt-24 bg-[#FAF9F5] py-20 sm:py-28">
      <div className="container-x">
        <div className="grain relative overflow-hidden rounded-[36px] bg-[#FCE4D4] p-6 sm:p-12 lg:p-16">
          <div className="anim-blob absolute -right-20 -top-20 h-80 w-80 rounded-full bg-[#FFB38A] opacity-50 blur-2xl" aria-hidden="true" />
          <div className="relative grid gap-12 lg:grid-cols-[1fr_1.15fr]">
            <Reveal>
              <h2 className="font-display text-4xl font-extrabold leading-[1.02] tracking-[-0.03em] text-slate-900 sm:text-5xl lg:text-6xl"><SplitWords text="Let's build something" /> <SplitWords text="amazing" delay={0.2} wordClass="text-[#C2410C]" /> <SplitWords text="together." delay={0.25} /></h2>
              <p className="mt-6 max-w-md text-base text-slate-700 sm:text-lg">Tell us what you need. We'll reply with ideas and next steps.</p>
              <ContactLinks />
            </Reveal>
            <Reveal delay={0.1}>
              {status === "done" ? (
                <div data-testid="contact-success" className="flex h-full flex-col items-start justify-center rounded-[28px] bg-white p-8 sm:p-10">
                  <CheckCircle2 className="h-14 w-14 text-[#84CC16]" />
                  <h3 className="mt-5 font-display text-3xl font-extrabold text-slate-900">Enquiry received!</h3>
                  <p className="mt-3 text-slate-600">Thanks for reaching out. We'll reply soon on the email or phone number you shared.</p>
                  <button data-testid="contact-send-another" onClick={() => setStatus("idle")} className="mt-6 rounded-full bg-slate-900 px-6 py-3 font-bold text-white hover:bg-[#0055FF]">Send another</button>
                </div>
              ) : (
                <form data-testid="contact-form" onSubmit={submit} noValidate className="space-y-4 rounded-[28px] bg-white/50 p-4 backdrop-blur-sm sm:p-6">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block"><span className="sr-only">Name</span><input data-testid="contact-name-input" className={field} placeholder="Your name *" value={form.name} onChange={set("name")} maxLength={100} /></label>
                    <label className="block"><span className="sr-only">Email or phone</span><input data-testid="contact-contact-input" className={field} placeholder="Email or phone number *" value={form.contact} onChange={set("contact")} maxLength={120} /></label>
                  </div>
                  <label className="block"><span className="sr-only">Service</span>
                    <select data-testid="contact-service-select" className={field} value={form.service} onChange={set("service")}>
                      <option value="">Service required</option>
                      {SITE.services.map((s) => <option key={s.name}>{s.name}</option>)}
                      <option>Other</option>
                    </select>
                  </label>
                  <label className="block"><span className="sr-only">Project description</span><textarea data-testid="contact-brief-input" className={`${field} min-h-[140px] resize-y`} placeholder="Short project description *" value={form.brief} onChange={set("brief")} maxLength={3000} /></label>
                  {err && <p data-testid="contact-error" role="alert" className="rounded-xl bg-white px-4 py-3 text-sm font-medium text-[#C2410C]">{err}</p>}
                  <button data-testid="contact-submit-button" type="submit" disabled={status === "sending"} className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-slate-900 px-7 py-4 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-[#0055FF] active:scale-[0.98] disabled:opacity-70">
                    {status === "sending" ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</> : <>Send enquiry <Send className="h-5 w-5 transition-transform group-hover:translate-x-1" /></>}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
